// RECALL-RETENTION-HOLD-FORGET (docs/design/2026-10-31-recall-full-integration.md §4.5, §7.1):
// what a company's memory keeps, holds and forgets.
//
// Simple for people: a member sees ONE thing, "forget this", on their own records
// (`memoryForget.*`). Everything else is for admins and owners (`memoryGov.*`) or the operator
// console (`operator.memoryGov.*`).
//
// None of the Recall doors behind these routes is live yet (they wait on Recall branches and the
// founder's go). Every action that needs one is checked first: when the memory service does not
// offer it the answer is HTTP 200 with `waiting` set (the words "Waiting on memory service"),
// never an error, and the request stands in CompanyOS until the door opens. What Recall allows a
// signed-in person only (a hold's release, naming legal contacts) is never done by us: the click
// files an operator job (`OperatorMemoryJob`).
import { z } from "zod";
import {
  MemoryForgetMode,
  MemoryForgetScope,
  MemoryForgetState,
  MemoryHoldScope,
  MemoryHoldState,
  MemoryRetentionState,
  OperatorJobKind,
  OperatorJobState,
} from "../enums";
import { boundedRecord, Timestamp, text, Uuid } from "../primitives";
import { CompanyParams } from "./common";
import { defineRoute } from "./route";

const co = "/v1/companies/{co}";
const memory = { feature: "memory", tag: "memory" } as const;
const operator = { feature: "core", tag: "operator", access: "operator" } as const;

/** What the memory service offers right now, and the words to show for what it does not. */
export const MemoryGovCaps = z
  .object({
    holds: z.boolean(),
    retention: z.boolean(),
    forget: z.boolean(),
    /** "Waiting on memory service": shown in place of any action that is not offered. */
    waiting_text: z.string(),
  })
  .meta({ id: "MemoryGovCaps" });
export type MemoryGovCaps = z.infer<typeof MemoryGovCaps>;

export const RetentionPolicy = z
  .strictObject({
    /** Working records cool after this many days. */
    cool_days: z.int().min(1).max(3650),
    /** Scratch-class captures expire after this many days. */
    scratch_days: z.int().min(1).max(365),
  })
  .meta({ id: "RetentionPolicy" });
export type RetentionPolicy = z.infer<typeof RetentionPolicy>;

export const RetentionDryRun = z
  .object({
    period: z.string().regex(/^\d{4}-\d{2}$/),
    at: Timestamp,
    total: z.int().min(0),
    rows: z.array(z.object({ space: z.string(), kind: z.string(), count: z.int().min(0) })),
  })
  .meta({ id: "RetentionDryRun" });
export type RetentionDryRun = z.infer<typeof RetentionDryRun>;

export const RetentionView = z
  .object({
    policy: RetentionPolicy,
    state: MemoryRetentionState,
    mirrored_at: Timestamp.nullable(),
    last_dry_run: RetentionDryRun.nullable(),
  })
  .meta({ id: "RetentionView" });

export type RetentionView = z.infer<typeof RetentionView>;

export const MemoryHoldView = z
  .object({
    id: Uuid,
    scope: MemoryHoldScope,
    /** The space's or the person's name; null for the workspace. */
    scope_name: z.string().nullable(),
    case_ref: z.string(),
    until: Timestamp.nullable(),
    state: MemoryHoldState,
    created_at: Timestamp,
  })
  .meta({ id: "MemoryHoldView" });
export type MemoryHoldView = z.infer<typeof MemoryHoldView>;

export const MemoryKeepView = z
  .object({
    id: Uuid,
    space_id: Uuid,
    space_name: z.string().nullable(),
    /** Empty: the whole space. */
    record_title: z.string(),
    note: z.string().nullable(),
    /** Recall has the pin; false: it stands here until the memory service offers it. */
    sent: z.boolean(),
    created_at: Timestamp,
  })
  .meta({ id: "MemoryKeepView" });

export type MemoryKeepView = z.infer<typeof MemoryKeepView>;

/** What the company asked the operator to do in Recall. */
export const MemoryJobView = z
  .object({
    id: Uuid,
    kind: OperatorJobKind,
    state: OperatorJobState,
    created_at: Timestamp,
    closed_at: Timestamp.nullable(),
  })
  .meta({ id: "MemoryJobView" });

export const ForgetPreviewView = z
  .object({
    count: z.int().min(0),
    by_kind: z.record(z.string(), z.int().min(0)),
    token_expires_at: Timestamp,
  })
  .meta({ id: "ForgetPreviewView" });

/** A forget request as its asker sees it; never the confirm token. */
export const ForgetRequestView = z
  .object({
    id: Uuid,
    mode: MemoryForgetMode,
    scope: MemoryForgetScope,
    label: z.string(),
    state: MemoryForgetState,
    preview: ForgetPreviewView.nullable(),
    /** A legal hold stands: the case it waits on. */
    case_ref: z.string().nullable(),
    /** Undo is offered until this instant (24 hours after the confirm). */
    undo_until: Timestamp.nullable(),
    /** A hard delete stays recoverable by the operator until this instant. */
    quarantine_until: Timestamp.nullable(),
    created_at: Timestamp,
  })
  .meta({ id: "ForgetRequestView" });
export type ForgetRequestView = z.infer<typeof ForgetRequestView>;

export const MemoryGovView = z
  .object({
    capabilities: MemoryGovCaps,
    retention: RetentionView.nullable(),
    holds: z.array(MemoryHoldView),
    /** True while a hold stands: forgets are deferred and the exit flow cannot archive or delete. */
    hold_banner: z.object({ active: z.boolean(), case_refs: z.array(z.string()) }),
    keeps: z.array(MemoryKeepView),
    jobs: z.array(MemoryJobView),
    /** Forget requests still open (previewed, blocked, waiting or inside the undo window). */
    forgets: z.array(ForgetRequestView),
  })
  .meta({ id: "MemoryGovView" });
export type MemoryGovView = z.infer<typeof MemoryGovView>;

/** The owner or an admin acts. A release and a legal contact are filed for the operator. */
export const MemoryGovAct = z
  .discriminatedUnion("action", [
    z.strictObject({
      action: z.literal("keep"),
      space_id: Uuid,
      /** Omitted: the whole space. */
      record_title: text(300).optional(),
      note: text(300).optional(),
    }),
    z.strictObject({ action: z.literal("unkeep"), keep_id: Uuid }),
    z.strictObject({
      action: z.literal("hold_release"),
      hold_id: Uuid,
      note: text(500).optional(),
    }),
    z.strictObject({
      action: z.literal("legal_contact"),
      name: text(200),
      email: z.email().max(320),
      note: text(500).optional(),
    }),
  ])
  .meta({ id: "MemoryGovAct" });
export type MemoryGovAct = z.infer<typeof MemoryGovAct>;

export const MemoryGovResult = z
  .object({
    /** Set when the memory service does not offer the door yet; the request stands. */
    waiting: z.string().nullable(),
    /** One plain sentence for what happened, e.g. that the operator was asked. */
    notice: z.string().nullable(),
    view: MemoryGovView,
  })
  .meta({ id: "MemoryGovResult" });
export type MemoryGovResult = z.infer<typeof MemoryGovResult>;

export const OffboardingView = z
  .object({
    /** False: the memory service does not offer the preview yet (`waiting` says so). */
    available: z.boolean(),
    waiting: z.string().nullable(),
    personal_records: z.int().min(0).nullable(),
    /** Company records the person wrote stay: they are the company's. */
    company_records_kept: z.int().min(0).nullable(),
    spaces: z.array(z.string()),
  })
  .meta({ id: "OffboardingView" });

export type OffboardingView = z.infer<typeof OffboardingView>;

export const MembershipParams = z.object({ co: Uuid, membership_id: Uuid });

// Forget ---------------------------------------------------------------------------------------

export const ForgetList = z
  .object({ items: z.array(ForgetRequestView), capabilities: MemoryGovCaps })
  .meta({ id: "ForgetList" });

/**
 * Preview, then confirm with the token, then undo inside 24 hours. A member may forget a record
 * in their own personal space (`scope: record`, `target: "<space id>/<type>/<title>"`); a person
 * or a whole space is for owners and admins.
 */
export const ForgetStep = z
  .discriminatedUnion("step", [
    z.strictObject({
      step: z.literal("preview"),
      scope: MemoryForgetScope,
      /** A record: `<space id>/<type>/<title>`; a person: a membership id; a space: a space id. */
      target: text(700),
    }),
    z.strictObject({ step: z.literal("confirm"), request_id: Uuid, token: text(200) }),
    z.strictObject({ step: z.literal("undo"), request_id: Uuid }),
    z.strictObject({ step: z.literal("dismiss"), request_id: Uuid }),
  ])
  .meta({ id: "ForgetStep" });
export type ForgetStep = z.infer<typeof ForgetStep>;

export const ForgetResult = z
  .object({
    request: ForgetRequestView,
    /** Returned once, with the preview; send it back to confirm. Never stored in the clear. */
    confirm_token: z.string().nullable(),
    waiting: z.string().nullable(),
  })
  .meta({ id: "ForgetResult" });
export type ForgetResult = z.infer<typeof ForgetResult>;

// Operator -------------------------------------------------------------------------------------

export const OperatorMemoryJob = z
  .object({
    id: Uuid,
    /** The console maps it to a name from its company list. */
    company_id: Uuid,
    kind: OperatorJobKind,
    state: OperatorJobState,
    /** What to do, in words and ids (a hold's case ref, the contact's name and email). */
    payload: z.record(z.string(), z.unknown()),
    ui_event_id: z.string(),
    created_at: Timestamp,
    recall_audit_id: z.string().nullable(),
    closed_at: Timestamp.nullable(),
  })
  .meta({ id: "OperatorMemoryJob" });
export type OperatorMemoryJob = z.infer<typeof OperatorMemoryJob>;

/**
 * The operator's memory settings: retention data per plan (a plan with no entry takes `default`)
 * and `memory.hard_delete_quarantine_days`, the quarantine a hard delete goes through (7 days).
 */
export const RetentionPlanData = z
  .strictObject({
    default: RetentionPolicy,
    plans: boundedRecord(RetentionPolicy, 20, z.string().min(1).max(60)),
    quarantine_days: z.int().min(1).max(90),
  })
  .meta({ id: "RetentionPlanData" });
export type RetentionPlanData = z.infer<typeof RetentionPlanData>;

export const OperatorMemoryGovView = z
  .object({
    retention: RetentionPlanData,
    /** Open jobs first, then the 50 most recently closed. */
    jobs: z.array(OperatorMemoryJob),
  })
  .meta({ id: "OperatorMemoryGovView" });
export type OperatorMemoryGovView = z.infer<typeof OperatorMemoryGovView>;

export const OperatorJobCloseBody = z
  .strictObject({ recall_audit_id: text(200) })
  .meta({ id: "OperatorJobCloseBody" });

export const OperatorHoldPlace = z
  .strictObject({
    scope: MemoryHoldScope,
    /** A space id or a membership id; omitted for the workspace. */
    scope_ref: Uuid.optional(),
    case_ref: text(120),
    /** Recall has no reason field: kept here and in the platform audit. */
    reason: text(500),
    until: Timestamp.optional(),
  })
  .meta({ id: "OperatorHoldPlace" });

export const OperatorHoldResult = z
  .object({ waiting: z.string().nullable(), hold: MemoryHoldView })
  .meta({ id: "OperatorHoldResult" });

export const OperatorDryRunResult = z
  .object({ waiting: z.string().nullable(), dry_run: RetentionDryRun.nullable() })
  .meta({ id: "OperatorDryRunResult" });

/**
 * A super admin's hard delete: the same preview, token, 24-hour undo as a forget, through Recall's
 * 7-day tombstone quarantine (`memory.hard_delete_quarantine_days`). The quarantine is skipped only
 * when a legal contact marked the delete as a security incident.
 */
export const OperatorHardDelete = z
  .discriminatedUnion("step", [
    z.strictObject({
      step: z.literal("preview"),
      scope: z.enum(["record", "space"]),
      target: text(700),
      /** Skips the quarantine. Needs `incident_ref` and a legal contact already named (a closed job). */
      security_incident: z.boolean().optional(),
      /** The legal contact's incident reference, kept in the audit. */
      incident_ref: text(200).optional(),
    }),
    z.strictObject({ step: z.literal("confirm"), request_id: Uuid, token: text(200) }),
    z.strictObject({ step: z.literal("undo"), request_id: Uuid }),
  ])
  .meta({ id: "OperatorHardDelete" });
export type OperatorHardDelete = z.infer<typeof OperatorHardDelete>;

const OperatorCompanyParams = z.object({ company_id: Uuid });
const JobParams = z.object({ company_id: Uuid, job_id: Uuid });
const AuditQuery = z.object({ format: z.enum(["json", "csv"]).optional() });

const EVENT = ["memory.governance_changed"] as const;

export const memoryGovernanceRoutes = {
  "memoryGov.get": defineRoute({
    ...memory,
    id: "memoryGov.get",
    method: "get",
    path: `${co}/memory/governance`,
    summary:
      "Retention, legal holds, keeps, operator requests and open forgets (owners and admins)",
    access: "manager",
    params: CompanyParams,
    response: { status: 200, schema: MemoryGovView },
    errors: [],
    emits: [],
  }),
  "memoryGov.act": defineRoute({
    ...memory,
    id: "memoryGov.act",
    method: "post",
    path: `${co}/memory/governance`,
    summary:
      "Keep a space or record against cooling, or ask the operator to release a hold or name a legal contact",
    description:
      "A hold's release and legal contacts are signed-in-only in Recall: they are filed as an operator job and never done here.",
    access: "manager",
    params: CompanyParams,
    body: MemoryGovAct,
    response: { status: 200, schema: MemoryGovResult },
    errors: ["not_found", "conflict"],
    emits: EVENT,
  }),
  "memoryGov.offboarding": defineRoute({
    ...memory,
    id: "memoryGov.offboarding",
    method: "get",
    path: `${co}/memory/offboarding/{membership_id}`,
    summary: "What leaving does to one person's memory: their own records, and what stays",
    access: "manager",
    params: MembershipParams,
    response: { status: 200, schema: OffboardingView },
    errors: ["not_found"],
    emits: [],
  }),
  "memoryGov.auditExport": defineRoute({
    ...memory,
    id: "memoryGov.auditExport",
    method: "get",
    path: `${co}/memory/audit-export`,
    summary: "Download the memory audit: holds, forgets, keeps and operator requests",
    access: "manager",
    params: CompanyParams,
    query: AuditQuery,
    response: { status: 200, contentType: "application/octet-stream" },
    errors: [],
    emits: [],
  }),
  "memoryForget.list": defineRoute({
    ...memory,
    id: "memoryForget.list",
    method: "get",
    path: `${co}/memory/forget`,
    summary: "The forgets you asked for (owners and admins also see everyone's)",
    access: "member",
    params: CompanyParams,
    response: { status: 200, schema: ForgetList },
    errors: [],
    emits: [],
  }),
  "memoryForget.step": defineRoute({
    ...memory,
    id: "memoryForget.step",
    method: "post",
    path: `${co}/memory/forget`,
    summary: "Forget this: preview, confirm with the token, undo within 24 hours",
    description:
      "Members forget records in their own personal space. A legal hold defers a forget and names its case.",
    access: "member",
    params: CompanyParams,
    body: ForgetStep,
    response: { status: 200, schema: ForgetResult },
    errors: ["not_found", "forbidden", "conflict"],
    emits: EVENT,
  }),
  "operator.memoryGov.get": defineRoute({
    ...operator,
    id: "operator.memoryGov.get",
    method: "get",
    path: "/v1/operator/memory-governance",
    summary: "Retention per plan, the hard-delete quarantine and the operator's memory jobs",
    response: { status: 200, schema: OperatorMemoryGovView },
    errors: [],
    emits: [],
  }),
  "operator.memoryGov.retentionPut": defineRoute({
    ...operator,
    id: "operator.memoryGov.retentionPut",
    method: "put",
    path: "/v1/operator/memory-governance/retention",
    summary: "Set the retention policy per plan; every company's tenant mirrors it",
    body: RetentionPlanData,
    response: { status: 200, schema: OperatorMemoryGovView },
    errors: [],
    emits: EVENT,
  }),
  "operator.memoryGov.jobClose": defineRoute({
    ...operator,
    id: "operator.memoryGov.jobClose",
    method: "post",
    path: "/v1/operator/memory-governance/companies/{company_id}/jobs/{job_id}/close",
    summary: "Close a job once the act is done in Recall, with Recall's audit id",
    params: JobParams,
    body: OperatorJobCloseBody,
    response: { status: 200, schema: OperatorMemoryGovView },
    errors: ["not_found", "conflict"],
    emits: EVENT,
  }),
  "operator.memoryGov.holdPlace": defineRoute({
    ...operator,
    id: "operator.memoryGov.holdPlace",
    method: "post",
    path: "/v1/operator/memory-governance/companies/{company_id}/holds",
    summary: "Place a legal hold on a space, a person or the workspace",
    description:
      "Placed with the install key (POST /install/holds). Its release is signed-in-only in Recall and is never done here.",
    params: OperatorCompanyParams,
    body: OperatorHoldPlace,
    response: { status: 200, schema: OperatorHoldResult },
    errors: ["not_found"],
    emits: EVENT,
  }),
  "operator.memoryGov.dryRun": defineRoute({
    ...operator,
    id: "operator.memoryGov.dryRun",
    method: "post",
    path: "/v1/operator/memory-governance/companies/{company_id}/retention-dry-run",
    summary: "Run the retention dry run for one company now (it also runs on the 1st of the month)",
    params: OperatorCompanyParams,
    response: { status: 200, schema: OperatorDryRunResult },
    errors: ["not_found"],
    emits: EVENT,
  }),
  "operator.memoryGov.hardDelete": defineRoute({
    ...operator,
    id: "operator.memoryGov.hardDelete",
    method: "post",
    path: "/v1/operator/memory-governance/companies/{company_id}/hard-delete",
    summary: "Delete for good, through Recall's 7-day quarantine (super admins only)",
    params: OperatorCompanyParams,
    body: OperatorHardDelete,
    response: { status: 200, schema: ForgetResult },
    errors: ["not_found", "forbidden", "conflict"],
    emits: EVENT,
  }),
} as const;
